/**
 * BatchProcessingContent - Deployment content for batch processing option
 *
 * Supports both images and video batch processing with:
 * - In-app batch job creation
 * - CLI instructions
 */

import React from "react";
import Button from "../../../../Button";
import { BATCH_PROCESSING_CLI_DOCS_LINK } from "../../DeployCodeGenerators";

type DataType = "images" | "video";

interface WorkflowInfo {
    workflowUrl: string;
    workflowName: string;
}

export interface BatchProcessingContentProps {
    dataType: DataType;
    workflowInfo: WorkflowInfo;
    onOpenBatchModal: (
        files: File[],
        config: { url: string; name: string; jobType: "images" | "videos" }
    ) => void;
    onClose: () => void;
    isWorkflowEditor?: boolean;
    onOpenTerminalScript?: (jobType: "images" | "videos") => void;
    hasExistingJobs?: boolean;
    onViewJobs?: (jobType: "images" | "videos") => void;
}

function BatchProcessingExplanation() {
    return (
        <div className="mb-4 text-sm text-gray-700">
            <p>
                Process large volumes of videos and images without writing code. Your images will be
                processed in jobs that run in the background.
            </p>
        </div>
    );
}

export function BatchProcessingContent({
    dataType,
    workflowInfo,
    onOpenBatchModal,
    onClose,
    isWorkflowEditor = true,
    onOpenTerminalScript,
    hasExistingJobs = false,
    onViewJobs
}: BatchProcessingContentProps) {
    const isVideo = dataType === "video";
    const jobType = isVideo ? "videos" : "images";

    return (
        <div className="space-y-6 p-2">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">Process Files in Bulk</h3>
                </div>
            </div>

            <BatchProcessingExplanation />

            <div className="rounded-md border border-gray-200 p-4">
                <h3 className="text-md mb-2 font-semibold text-gray-800">
                    {isWorkflowEditor
                        ? "Create Job In-App"
                        : `Upload ${isVideo ? "Videos" : "Images"} in Browser`}
                </h3>
                <p className="mb-3 text-sm text-gray-600">
                    Upload {isVideo ? "videos" : "images"} directly through the Roboflow platform to
                    start a processing job.
                </p>
                <div className="flex gap-2">
                    <Button
                        type="primary"
                        onClick={() => {
                            onOpenBatchModal([], {
                                url: workflowInfo.workflowUrl,
                                name: workflowInfo.workflowName,
                                jobType
                            });
                            if (isWorkflowEditor) onClose();
                        }}
                    >
                        {isWorkflowEditor ? (
                            "Create In-App Batch Job"
                        ) : (
                            <>
                                <i className="far fa-upload"></i>
                                Upload {isVideo ? "Videos" : "Images"}
                            </>
                        )}
                    </Button>
                    {!isWorkflowEditor && hasExistingJobs && onViewJobs && (
                        <Button type="secondary" onClick={() => onViewJobs(jobType)}>
                            <i className="far fa-list-check"></i>
                            View Jobs
                        </Button>
                    )}
                </div>
            </div>

            <div className="rounded-md border border-gray-200 p-4">
                <h3 className="text-md mb-2 font-semibold text-gray-800">
                    Process via Command Line
                </h3>
                <p className="mb-3 text-sm text-gray-600">
                    Use our CLI tools to process {isVideo ? "video " : ""}files from your terminal.
                </p>
                <div className="flex gap-2">
                    <Button
                        type="secondary"
                        onClick={() => {
                            window.open(BATCH_PROCESSING_CLI_DOCS_LINK, "_blank");
                        }}
                    >
                        <i className="far fa-terminal mr-2"></i>
                        Show CLI Documentation
                    </Button>
                    {onOpenTerminalScript && (
                        <Button
                            type="secondary"
                            onClick={() => {
                                onOpenTerminalScript(jobType);
                            }}
                        >
                            <i className="far fa-code mr-2"></i>
                            Get Terminal Script
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
